import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/components/AuthContext";
import { Package, EnvelopeSimple, Lock, User, CaretDown, WarningCircle, ArrowLeft, Crown, ShieldCheck, Truck, Storefront } from "@phosphor-icons/react";

function formatApiError(detail) {
  if (detail == null) return "Algo correu mal. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const PORTAL_VISUAL = {
  user:         { color: "#2D4A3E", icon: User,        labelKey: "portal.user_title",         demo: { email: "user@blumove.pt",         password: "User2026!" } },
  intermediary: { color: "#0ea5e9", icon: Storefront,  labelKey: "portal.intermediary_title", demo: { email: "intermediary@blumove.pt", password: "Inter2026!" } },
  driver:       { color: "#f59e0b", icon: Truck,       labelKey: "portal.driver_title",       demo: { email: "driver@blumove.pt",       password: "Driver2026!" } },
  admin:        { color: "#8b5cf6", icon: ShieldCheck, labelKey: "portal.admin_title",        demo: { email: "admin@blumove.pt",        password: "BluMove2026!" } },
  super_admin:  { color: "#dc2626", icon: Crown,       labelKey: "portal.super_admin_title",  demo: { email: "superadmin@blumove.pt",   password: "SuperAdmin2026!" } },
};

export default function AuthPage({ onSkipDemo, role = null, onBack }) {
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: role || "user" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Lock register-role picker when entered via portal
  const lockRoleToPortal = !!role;
  const portalVisual = role ? PORTAL_VISUAL[role] : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (isLogin) {
        await login(form.email, form.password, role);
      } else {
        await register(form.name, form.email, form.password, lockRoleToPortal ? role : form.role);
      }
    } catch (err) {
      if (err?.code === "ROLE_MISMATCH" && role) {
        setError(t("portal.role_mismatch", { expected: t(PORTAL_VISUAL[role].labelKey), got: err.actualRole }));
      } else {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px 12px 42px", borderRadius: 12,
    border: "1px solid var(--border-color)", fontFamily: "Manrope",
    fontSize: "0.9rem", background: "var(--bg)", outline: "none",
  };

  const ROLES = [
    { id: "user", label: "Utilizador", desc: "Enviar e receber encomendas" },
    { id: "intermediary", label: "Intermediario", desc: "Gerir ponto de recolha/entrega" },
    { id: "driver", label: "Motorista", desc: "Entregas e rotas" },
  ];

  return (
    <div data-testid="auth-page" data-testid-role={role || "any"} style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 440, width: "100%" }}>
        {/* Logo */}
        <div className="flex items-center gap-3 justify-center mb-6">
          <img src="/assets/logo.png" alt="Bluemove" style={{ height: 44 }} />
        </div>

        {/* Back to portal */}
        {onBack && (
          <button
            data-testid="auth-back-btn"
            onClick={onBack}
            className="flex items-center gap-2"
            style={{ margin: "0 auto 14px", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "0.8rem" }}
          >
            <ArrowLeft size={14} /> {t("portal.back_to_choose")}
          </button>
        )}

        <div className="bento-item p-8" style={{ borderTop: portalVisual ? `3px solid ${portalVisual.color}` : undefined }}>
          {/* Portal context banner */}
          {portalVisual && (
            <div className="flex items-center gap-3 mb-4" data-testid="auth-portal-banner" style={{ padding: "10px 12px", borderRadius: 10, background: `${portalVisual.color}10` }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${portalVisual.color}26`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <portalVisual.icon size={20} weight="duotone" style={{ color: portalVisual.color }} />
              </div>
              <div>
                <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 1 }}>{t("portal.portal_label")}</div>
                <div style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "0.95rem", color: portalVisual.color, lineHeight: 1.2 }}>{t(portalVisual.labelKey)}</div>
              </div>
            </div>
          )}

          <h2 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.5rem", textAlign: "center", marginBottom: 4 }}>
            {isLogin ? t("auth.title") : t("common.register")}
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", textAlign: "center", marginBottom: 24 }}>
            {isLogin ? t("auth.subtitle") : "Registe-se para comecar a usar"}
          </p>

          {error && (
            <div data-testid="auth-error" className="flex items-center gap-2 mb-4" style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(184,74,74,0.08)", color: "var(--error)", fontSize: "0.85rem" }}>
              <WarningCircle size={18} weight="fill" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {!isLogin && (
              <div style={{ position: "relative" }}>
                <User size={18} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input data-testid="register-name-input" placeholder="Nome completo" required style={inputStyle} value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
              </div>
            )}

            <div style={{ position: "relative" }}>
              <EnvelopeSimple size={18} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input data-testid="auth-email-input" type="email" placeholder="Email" required style={inputStyle} value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
            </div>

            <div style={{ position: "relative" }}>
              <Lock size={18} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input data-testid="auth-password-input" type="password" placeholder="Password" required style={inputStyle} value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
            </div>

            {!isLogin && !lockRoleToPortal && (
              <div>
                <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Tipo de conta</label>
                <div className="flex flex-col gap-2">
                  {ROLES.map(r => (
                    <label key={r.id} data-testid={`role-option-${r.id}`} className="flex items-center gap-3 cursor-pointer" style={{ padding: "10px 14px", borderRadius: 10, border: `2px solid ${form.role === r.id ? "var(--primary)" : "var(--border-color)"}`, background: form.role === r.id ? "rgba(45,74,62,0.04)" : "transparent", transition: "all 0.2s" }}>
                      <input type="radio" name="role" value={r.id} checked={form.role === r.id} onChange={e => setForm({...form, role: e.target.value})} style={{ display: "none" }} />
                      <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${form.role === r.id ? "var(--primary)" : "var(--border-color)"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {form.role === r.id && <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--primary)" }} />}
                      </div>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>{r.label}</span>
                        <span style={{ color: "var(--text-muted)", fontSize: "0.75rem", marginLeft: 8 }}>{r.desc}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={submitting}
              style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", cursor: "pointer", opacity: submitting ? 0.6 : 1, transition: "background 0.25s" }}
              onMouseEnter={e => { if (!submitting) e.target.style.background = "var(--primary-hover)"; }}
              onMouseLeave={e => e.target.style.background = "var(--primary)"}
            >
              {submitting ? "..." : isLogin ? t("auth.title") : t("common.register")}
            </button>
          </form>

          <div className="text-center mt-4">
            <button
              data-testid="toggle-auth-mode-btn"
              onClick={() => { setIsLogin(!isLogin); setError(""); }}
              style={{ background: "none", border: "none", color: "var(--primary)", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem" }}
            >
              {isLogin ? `${t("auth.no_account")} ${t("auth.create")}` : `${t("common.login")}`}
            </button>
          </div>

          {/* Demo credentials helper — visible only on login + when portal is selected */}
          {isLogin && portalVisual?.demo && (
            <div data-testid="auth-demo-creds" style={{ marginTop: 18, padding: 12, borderRadius: 12, background: `${portalVisual.color}10`, border: `1px dashed ${portalVisual.color}55` }}>
              <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                <span style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: portalVisual.color }}>
                  {t("auth.demo_creds_title")}
                </span>
                <button
                  data-testid="auth-demo-fill-btn"
                  type="button"
                  onClick={() => setForm(f => ({ ...f, email: portalVisual.demo.email, password: portalVisual.demo.password }))}
                  style={{ background: portalVisual.color, color: "white", border: "none", padding: "5px 12px", borderRadius: 9999, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", fontFamily: "Outfit" }}
                >
                  {t("auth.demo_creds_use")}
                </button>
              </div>
              <div style={{ fontFamily: "JetBrains Mono", fontSize: "0.74rem", color: "var(--text-main)", lineHeight: 1.6 }}>
                <div><span style={{ color: "var(--text-muted)" }}>email</span>  <strong>{portalVisual.demo.email}</strong></div>
                <div><span style={{ color: "var(--text-muted)" }}>pass</span>  <strong>{portalVisual.demo.password}</strong></div>
              </div>
              <p style={{ marginTop: 6, fontSize: "0.65rem", color: "var(--text-muted)" }}>
                {t("auth.demo_creds_hint")}
              </p>
            </div>
          )}
        </div>

        {/* Demo mode */}
        <div className="text-center mt-4">
          <button
            data-testid="skip-to-demo-btn"
            onClick={onSkipDemo}
            style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "0.8rem", textDecoration: "underline" }}
          >
            {t("auth.demo_link")}
          </button>
        </div>
      </div>
    </div>
  );
}
